import {
  Button,
  Box,
  CircularProgress,
  Link,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material'
import moment from 'moment/moment'
import React, { useState } from 'react'
import { useBoolean } from 'react-use'

import agent from 'agent'
import Section from 'components/common/Section'
import Icon from 'components/Icon'
import { convertCentsToDollars } from 'components/services/service-utils'
import useAuth from 'hooks/use-auth'
import { useFeatureFlags } from 'hooks/use-feature-flags'
import { trans } from 'i18n/translate'
import toast from 'lib/toast'
import { PaywallStatusKey } from 'types/api/user/session'
import { messageFromResponseError } from 'utils/strings'

export interface PaymentInformation {
  invoiceDate: string
  billingStartDate?: string
  billingEndDate?: string
  amountInCents: number
  servicesConsumed: number
}

export const handlePaymentLinkRequest = async (
  setIsRequestingLink,
  currentTeamId
) => {
  const stripePaymentLinkEndpoint = `/payments/billing-portal?teamId=${currentTeamId}`
  setIsRequestingLink(true)
  try {
    const stripeEndpointResponse = await agent.get(stripePaymentLinkEndpoint)
    // calling it this way makes it more easily testable.
    window.location.assign(stripeEndpointResponse.data)
  } catch (e) {
    toast({
      type: 'error',
      text: 'Could not resolve payment link.',
      testKey: 'portal-error'
    })
  }
  setIsRequestingLink(false)
}

export const Payment: React.FC = () => {
  const [requestingLink, setRequestingLink] = useState(false)
  const [isModalOpen, setIsModalOpen] = useBoolean(false)
  const [isSubmitting, setIsSubmitting] = useBoolean(false)

  const {
    RBAC,
    currentTeamId,
    fetchSession,
    hasCompanyPrivilege,
    isRMSubscription,
    isTrial,
    paymentInfo,
    paywallStatus
  } = useAuth()

  const handleCancelSubscription = async () => {
    try {
      await agent.post('/companies/cancel')

      await fetchSession()
    } catch (e) {
      const error = messageFromResponseError(
        e,
        trans('rbac_settings.payment.cancel_error.text')
      )
      toast({
        type: 'error',
        text: error,
        testKey: 'handle-cancellation-error'
      })
    }

    setIsModalOpen(false)
    setIsSubmitting(false)
  }

  const { cancelSubscription: isCancelSubscriptionEnabled } = useFeatureFlags()

  const showCancelButton =
    // if paywallStatus is not null, this subscription may already be cancelled or
    // there may be other payment issues we want them to take care of first before cancellation
    // i.e. invalid payment method, outstanding invoices
    isCancelSubscriptionEnabled &&
    !paywallStatus &&
    hasCompanyPrivilege(RBAC.privileges.company.CANCEL_RM_SUBSCRIPTION)

  return (
    <Section>
      <Box data-test-company-subscription-payment p={2} sx={{ marginLeft: 2 }}>
        <Stack spacing={4}>
          <Stack pt={4}>
            {(paywallStatus?.key as PaywallStatusKey) ===
              'CANCELLED_BY_USER' && (
              <Typography data-test-payment-cancelled-by-user-message>
                {paywallStatus.message}
              </Typography>
            )}
            <Stack direction="row" pt={4} spacing={8}>
              <Stack spacing={2}>
                <Typography>
                  {trans(
                    'rbac_settings.payment.service_next_invoice_date.text'
                  )}
                </Typography>
                <Typography
                  data-test-subscription-data="next-invoice-date"
                  variant="h6"
                >
                  {paymentInfo?.invoiceDate
                    ? moment(paymentInfo.invoiceDate).format('MMM D, YYYY')
                    : '--'}
                </Typography>
              </Stack>
              <Stack spacing={2}>
                <Typography>
                  {trans('rbac_settings.payment.service_invoice_total.text')}
                </Typography>
                <Typography
                  data-test-subscription-data="invoice-total"
                  variant="h6"
                >
                  {paymentInfo?.amountInCents
                    ? convertCentsToDollars(paymentInfo.amountInCents)
                    : '--'}
                </Typography>
              </Stack>
              <Stack spacing={2}>
                <Typography>
                  {trans('rbac_settings.payment.service_consumed.text')}
                </Typography>
                <Typography
                  data-test-subscription-data="number-of-services"
                  variant="h6"
                >
                  {paymentInfo?.servicesConsumed ?? '--'}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
          {isRMSubscription && !isTrial && (
            <Link
              data-test-external-payment-link
              data-disabled={requestingLink}
              onClick={() =>
                handlePaymentLinkRequest(setRequestingLink, currentTeamId)
              }
            >
              {trans('rbac_settings.payment.external_payment_link.text')}
              {requestingLink && (
                <CircularProgress size={14} sx={{ marginLeft: 1.5 }} />
              )}
            </Link>
          )}
          {showCancelButton && (
            <Button
              data-test-payment-cancel-button
              color="error"
              css={{ width: 'fit-content' }}
              startIcon={<Icon icon="xmark" />}
              variant="contained"
              onClick={() => setIsModalOpen(true)}
            >
              {trans('rbac_settings.payment.cancel_button.text')}
            </Button>
          )}
          <Dialog
            aria-labelledby="alert-dialog-title"
            open={isModalOpen}
            onClose={() => setIsModalOpen(false)}
          >
            <DialogTitle
              data-test-payment-cancel-modal-title
              color="error"
              id="alert-dialog-title"
              variant="h6"
            >
              {trans('rbac_settings.payment.cancel_modal.title')}
            </DialogTitle>
            <DialogContent>
              <DialogContentText
                data-test-payment-cancel-modal-text
                color="text"
                variant="body1"
              >
                {trans('rbac_settings.payment.cancel_modal.text')}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                data-test-payment-cancel-modal-back-button
                onClick={() => setIsModalOpen(false)}
              >
                {trans('rbac_settings.payment.cancel_modal.back_button')}
              </Button>
              <Button
                autoFocus
                data-test-payment-cance-modal-cancel-button
                disabled={isSubmitting}
                onClick={handleCancelSubscription}
              >
                {trans('rbac_settings.payment.cancel_modal.cancel_button')}
              </Button>
            </DialogActions>
          </Dialog>
        </Stack>
      </Box>
    </Section>
  )
}
